import request from "../helpers/request";

/** Company panel üçün */
export const getCompanyNotifications = async () => {
  const response = await request.get("/api/company/notifications");
  return response.data;
};

export const markNotificationAsRead = async (notificationId) => {
  const response = await request.post(
    `/api/company/notifications/${notificationId}/mark-as-read`
  );
  return response.data;
};

function pickField(item, ...names) {
  if (!item || typeof item !== "object") return undefined;
  for (const name of names) {
    if (item[name] != null && item[name] !== "") return item[name];
    const foundKey = Object.keys(item).find(
      (key) => key.toLowerCase() === String(name).toLowerCase()
    );
    if (foundKey && item[foundKey] != null && item[foundKey] !== "") {
      return item[foundKey];
    }
  }
  return undefined;
}

function pickFieldWithDetail(item, detail, ...names) {
  return pickField(item, ...names) ?? (detail ? pickField(detail, ...names) : undefined);
}

function parseAppointmentIdFromText(value) {
  if (value == null) return null;
  const text = String(value);
  const direct = text.match(/(?:appointment|randevu)[^\d]{0,12}(\d+)/i);
  if (direct) return direct[1];
  const idLike = text.match(/\b(?:id|#)\s*[:=-]?\s*(\d+)\b/i);
  return idLike ? idLike[1] : null;
}

export function extractAppointmentIdFromNotification(item) {
  if (!item || typeof item !== "object") return null;
  const detail = item?.detail && typeof item.detail === "object" ? item.detail : null;

  const fromFields = pickFieldWithDetail(
    item,
    detail,
    "appointment_id",
    "appointmentId",
    "appointmentid",
    "booking_id",
    "bookingId",
    "order_id",
    "related_id",
    "entity_id"
  );
  if (fromFields != null && String(fromFields).trim() !== "") return String(fromFields);

  return parseAppointmentIdFromText(
    pickFieldWithDetail(
      item,
      detail,
      "content",
      "message",
      "body",
      "text",
      "description",
      "title"
    )
  );
}

/** API "28-06-2026 03:07" (DD-MM-YYYY HH:mm) və ISO formatlarını parse edir */
export function parseNotificationDate(value) {
  if (value == null || value === "") return null;

  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) return direct;

  const match = String(value).match(
    /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!match) return null;

  const [, day, month, year, hour = "0", minute = "0", second = "0"] = match;
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  );
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * API cavabından bildiriş massivini çıxarır.
 * Laravel paginator və nested `data.notifications` strukturlarını dəstəkləyir.
 */
export function parseCabinetNotificationsList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload.data,
    payload.notifications,
    payload.items,
    payload.results,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (candidate && typeof candidate === "object") {
      const nested = [
        candidate.data,
        candidate.notifications,
        candidate.items,
        candidate.results,
      ];
      for (const entry of nested) {
        if (Array.isArray(entry)) return entry;
      }
    }
  }

  return [];
}

export function parseNotificationIsRead(item) {
  if (item && typeof item.isRead === "boolean") return item.isRead;

  const readAt = pickField(item, "read_at", "readAt", "read_at_date");
  if (readAt) return true;

  const raw = pickField(item, "is_read", "read", "isRead", "is_readed");
  if (raw === true || raw === 1 || raw === "1") return true;
  if (raw === false || raw === 0 || raw === "0") return false;
  return Boolean(raw);
}

export function countUnreadNotifications(list) {
  if (!Array.isArray(list)) return 0;
  return list.filter((item) => !parseNotificationIsRead(item)).length;
}

export function parseUnreadCountFromPayload(payload, list) {
  const candidates = [
    payload?.unread_count,
    payload?.unreadCount,
    payload?.unread,
    payload?.meta?.unread_count,
    payload?.meta?.unreadCount,
    payload?.data?.unread_count,
    payload?.data?.unreadCount,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }

  return countUnreadNotifications(list);
}

export function formatNotificationCount(count) {
  const parsed = Number(count);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  if (parsed > 99) return "99+";
  return String(parsed);
}

export function normalizeCabinetNotification(item, { defaultTitle, dateLocale }) {
  const detail =
    item?.detail && typeof item.detail === "object" ? item.detail : null;
  const appointmentId = extractAppointmentIdFromNotification(item);

  const createdAt = pickFieldWithDetail(
    item,
    detail,
    "created_at",
    "createdAt",
    "date",
    "sent_at",
    "sentAt",
    "updated_at"
  );

  let dateStr = "";
  let timeStr = "";

  const parsedDate = parseNotificationDate(createdAt);
  if (parsedDate) {
    dateStr = parsedDate.toLocaleDateString(dateLocale);
    timeStr = parsedDate.toLocaleTimeString(dateLocale, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return {
    id: pickField(item, "id", "notification_id", "notificationId"),
    title:
      pickFieldWithDetail(item, detail, "title", "subject", "heading", "name") ||
      defaultTitle,
    content:
      pickFieldWithDetail(
        item,
        detail,
        "content",
        "message",
        "body",
        "text",
        "description"
      ) || "",
    type: pickFieldWithDetail(item, detail, "type") || "",
    appointmentId,
    date: dateStr,
    time: timeStr,
    isRead: parseNotificationIsRead(item),
  };
}

/** Profil (cabinet) bildirişləri – istifadəçinin öz bildirişləri */
export const getCabinetNotifications = async (params = {}) => {
  const response = await request.get("/api/cabinet/notifications", {
    params,
  });
  return response.data;
};

export const markCabinetNotificationAsRead = async (notificationId) => {
  const url = `/api/cabinet/notifications/${notificationId}/mark-as-read`;

  try {
    const response = await request.post(url);
    return response.data;
  } catch (error) {
    const status = error.response?.status;
    if (status === 405 || status === 404) {
      const response = await request.put(url);
      return response.data;
    }
    throw error;
  }
};
